
import React from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TeamSeason } from '../types/selector-types';

interface SeasonSelectorProps {
  availableSeasons: TeamSeason[];
  selectedSeasons: string[];
  onSeasonToggle: (season: string) => void;
  onClearSelection: () => void;
}

export const SeasonSelector: React.FC<SeasonSelectorProps> = ({
  availableSeasons,
  selectedSeasons,
  onSeasonToggle,
  onClearSelection,
}) => {
  // Function to display league name without the numeric prefix and preserving "Premier"
  const displayLeagueName = (league: string) => {
    const match = league.match(/^\d{2}\s*(.+)$/);
    return match ? match[1] : league;
  };

  const handleSelectAll = () => {
    // If all seasons are already selected, do nothing
    if (selectedSeasons.length === availableSeasons.length) return;
    
    // Otherwise, select all seasons
    availableSeasons.forEach(season => {
      if (!selectedSeasons.includes(season.season)) {
        onSeasonToggle(season.season);
      }
    });
  };

  const handleClearSelection = () => {
    // Clear current selection and select only the most recent season
    if (availableSeasons.length > 0) {
      onClearSelection();
      onSeasonToggle(availableSeasons[0].season);
    }
  };

  const allSeasonsSelected = selectedSeasons.length === availableSeasons.length;

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-xl font-semibold">Select Seasons</h2>
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSelectAll}
            disabled={allSeasonsSelected}
          >
            Select All
          </Button>
          {selectedSeasons.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearSelection}
              className="text-red-500 hover:text-red-700"
            >
              Clear Selection
            </Button>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {availableSeasons.map(({ season, league }) => (
          <div key={season} className="flex flex-col items-start gap-1">
            <Button
              variant={selectedSeasons.includes(season) ? "secondary" : "outline"}
              onClick={() => onSeasonToggle(season)}
              size="sm"
              className="flex items-center gap-1"
            >
              {season}
              {selectedSeasons.includes(season) && (
                <Badge variant="secondary" className="ml-1 text-xs">Selected</Badge>
              )}
            </Button>
            <span className="text-xs text-muted-foreground px-2">{displayLeagueName(league)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
